import { EventAggregator } from "aurelia-event-aggregator";
import { autoinject, Disposable } from "aurelia-framework";
import { Router, RouterConfiguration } from "aurelia-router";
import jwtDecode from "jwt-decode";

import { AccessService } from "../../services/access.service";
import AuthService from "../../services/auth.service";
import { UserService } from "../../services/user-service";
import { HttpService } from "../../services/http.service";
import { IdleTimer } from "../../services/idle-timer.service";
import { RolesService } from "../../services/roles.service";

import type { IRoles } from "../../resources-vue/vue-interfaces/i-roles";
import { Role } from "../../models/role.model";

import config from "../../common/config";
import { OidcConfiguration } from "../../common/oidc-configuration";
import { UserLoaded } from "../../common/oidc/oidc-event-handler";
import { PermissionManager } from "../../common/utilities/permission-manager";
import { TestingAgencyHelper, helperTestingAgency } from "../../common/vue-helpers/testing-agency-check";

import { AppCache } from "./app-cache";
import defaultRoutes from "./app-routes";
import { AuthorizeStep } from "./authorize-step";
import { PageTakeOverLoading } from "./loading-indicator";
import { MarketingContent } from "./marketing-content";

import "./app.scss";
import "bootstrap-datepicker/dist/css/bootstrap-datepicker.css";

export interface ILocationSave {
    fragment: string;
    query: string;
}
@autoinject
export class App {
    private _ea: EventAggregator;
    private _rolesService: RolesService;
    private _authService: AuthService;
    private _userService: UserService;
    private _idleTimerService: IdleTimer;
    private _httpService: HttpService;
    private _marketingContent: MarketingContent;
    private _oidcConfiguration: OidcConfiguration;
    private _accessService: AccessService;
    private _subscription: Disposable = null;
    private _appCache: AppCache;
    private _permissionManager: PermissionManager;
    public roles: IRoles[] = [];
    public router: Router;
    public isLoggedIn: boolean = false;
    public isLoading: boolean = false;
    public areRolesLoaded: boolean = false;
    public isShowingVue: boolean = false;

    public constructor(
        ea: EventAggregator,
        idleTimerService: IdleTimer,
        rolesService: RolesService,
        authService: AuthService,
        userService: UserService,
        httpService: HttpService,
        marketingContent: MarketingContent,
        accessService: AccessService,
        oidcConfiguration: OidcConfiguration,
        appCache: AppCache,
        permissionsManager: PermissionManager
    ) {
        this._ea = ea;
        this._idleTimerService = idleTimerService;
        this._rolesService = rolesService;
        this._authService = authService;
        this._userService = userService;
        this._accessService = accessService;
        this._httpService = httpService;
        this._marketingContent = marketingContent;
        this._httpService.configureFetch();
        this._oidcConfiguration = oidcConfiguration;
        this._appCache = appCache;
        this.initSubscriptions();
        this._permissionManager = permissionsManager;
    }

    public async activate() {
        this.getToken();
        // If the user is already loaded
        await this.initUser();
    }

    public initSubscriptions() {
        this._subscription = this._ea.subscribe(UserLoaded, async () => {
            // When redirecting from identity, User will be loaded after app initiates
            await this.initUser();
        });
    }

    private async initUser() {
        this.isLoading = true;
        let isUserLoggedIn = await this._authService.isLoggedIn();
        if (!isUserLoggedIn) {
            return;
        }
        if (TestingAgencyHelper?.isVueAppMounted) {
            return;
        }
        const canShowVueApp = await helperTestingAgency.shouldMountVue();

        if (canShowVueApp) {
            const vueMounted = await helperTestingAgency.mountVueApp();
            if (vueMounted) {
                this.isShowingVue = true;
                this.isLoading = false;
                return;
            }
        }
        this.isLoggedIn = true;
        await this._permissionManager.refreshUserPermissionData();
        this.roles = await this.getUserRoles();
        this._accessService.initAccessRoles(this.roles);
        await this._appCache.restoreMissingCache();
        // Adding this property to pass to app-nav to know when roles are loaded
        this.areRolesLoaded = true;
        this.isLoading = false;
    }

    private async getUserRoles() {
        try {
            let roles: Array<IRoles> = await this._rolesService.getRoles();
            return roles.map((role) => new Role(role));
        } catch (e) {
            // Roles endpoint should not be a bottle neck.
            // By returning an empty array, the user will at least see the help page.
            // Where they can select an agency.
            return [];
        }
    }

    public async configureRouter(routerConfig: RouterConfiguration, router: Router) {
        this.router = router;
        routerConfig.title = "Axxess Router";
        routerConfig.options.pushState = true;
        routerConfig.map(defaultRoutes);
        routerConfig.mapUnknownRoutes("/help");
        routerConfig.fallbackRoute("/help");
        routerConfig.options.root = "/";
        this._oidcConfiguration.configureRouter(routerConfig, router);
        routerConfig.addAuthorizeStep(AuthorizeStep);
    }

    public async attached() {
        try {
            this._ea.publish(PageTakeOverLoading.Hide);
            await this._idleTimerService.timer();
            await this.startMarketingContent();
        } catch (error) {
            console.error(error);
        } finally {
            this._ea.publish(PageTakeOverLoading.Hide);
        }
    }

    public getToken() {
        const url = window.location.href;
        const isTokenPresent = url.indexOf("live_token") > -1;

        if (isTokenPresent) {
            const accessToken = url.split("live_token=")[1];

            let save: ILocationSave = {
                fragment: "",
                query: accessToken
            };

            window.sessionStorage.setItem(config.locationSaveKey, JSON.stringify(save));

            const decoded = jwtDecode(accessToken);

            let openIdObject = {
                id_token: accessToken,
                session_state: "sEULhiGvQLQ7OifmmXk32YsGycaCuidJSeBDMqt88Ew.E6B978FD5942BF43FAFFA89485E08F02",
                access_token: accessToken,
                token_type: "Bearer",
                scope: "openid profile email",
                profile: decoded,
                expires_at: ""
            };

            window.sessionStorage.setItem(
                config.isDev || config.isStage
                    ? "oidc.user:https://identity.test.axxess.tech/.well-known/openid-configuration:Axxess-Router-Frontend"
                    : "oidc.user:https://identity.axxessweb.com/.well-known/openid-configuration:Axxess-Router-Frontend",
                JSON.stringify(openIdObject)
            );
        }
    }

    private async startMarketingContent() {
        await this._marketingContent?.start();
    }

    public deactivate() {
        this._subscription?.dispose();
    }
}
